import { getPlatform, isNativePlatform } from '@/lib/utils/platform'

/**
 * Native system bar styling (ALU-401, ALU-403).
 *
 * Naming follows the Capacitor plugins: the value names the BACKGROUND, so
 * `Light` = dark icons for a light surface and `Dark` = white icons for a dark one.
 * Every call is best effort: on the web or in an older shell the plugin is missing.
 */

/** Surfaces that are dark under the status bar and have asked for white icons. */
let darkSurfaces = 0

async function setStatusBar(style: 'LIGHT' | 'DARK') {
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar')
    await StatusBar.setStyle({ style: style === 'LIGHT' ? Style.Light : Style.Dark })
  } catch {
    // Plugin not available: nothing to adjust.
  }
}

/**
 * Android: Capacitor 8's core `SystemBars` plugin owns BOTH bars, applies its own
 * DEFAULT style at load and on every configuration change, and nothing else sets the
 * navigation-bar icons. Driving it keeps the style across rotation and theme changes.
 */
async function setAndroidSystemBars(style: 'LIGHT' | 'DARK', onlyStatusBar: boolean) {
  try {
    if (getPlatform() !== 'android') return
    const { SystemBars, SystemBarsStyle, SystemBarType } = await import('@capacitor/core')
    await SystemBars.setStyle({
      style: style === 'LIGHT' ? SystemBarsStyle.Light : SystemBarsStyle.Dark,
      ...(onlyStatusBar ? { bar: SystemBarType.StatusBar } : {}),
    })
  } catch {
    // Core plugin not available: nothing to adjust.
  }
}

/** Every page after the brand-blue launch is light: dark clock, icons and handle. */
export async function setSystemBarsForLightPages() {
  darkSurfaces = 0
  await Promise.all([setStatusBar('LIGHT'), setAndroidSystemBars('LIGHT', false)])
}

/**
 * The brand-blue loader (first launch and every web reload). iOS draws the
 * WebView under the status bar, so the clock sits on blue: white icons. On
 * Android (edge-to-edge, API 35+) the WebView starts BELOW the bars and the
 * strip behind them is the light window background, so white icons vanish
 * (build 16 test pass: clock almost invisible for seconds). Keep them dark there.
 */
export async function setSystemBarsForLoader() {
  const android = getPlatform() === 'android'
  await Promise.all([
    setStatusBar(android ? 'LIGHT' : 'DARK'),
    setAndroidSystemBars(android ? 'LIGHT' : 'DARK', true),
  ])
}

/**
 * A dark surface (offline banner, image lightbox) sits under the status bar: white
 * icons while it is open. Returns the release function, which restores dark icons only
 * when no other dark surface is still open.
 */
export function holdStatusBarOverDark(): () => void {
  if (!isNativePlatform()) return () => {}
  darkSurfaces += 1
  void Promise.all([setStatusBar('DARK'), setAndroidSystemBars('DARK', true)])
  let released = false
  return () => {
    if (released) return
    released = true
    darkSurfaces = Math.max(0, darkSurfaces - 1)
    if (darkSurfaces === 0) {
      void Promise.all([setStatusBar('LIGHT'), setAndroidSystemBars('LIGHT', true)])
    }
  }
}
